/**
 * SphereCreatedContent - Renders content for SphereCreated timeline events
 * Shows who created the sphere (with avatar), when, and the sphere type
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'localization';
import { TimelineEvent } from 'types/timeline';
import { useApiClientsOrNull } from '../../../../context/ApiClientsContext';
import { globalSphereCache } from '@center-platform/spheres/cache';
import SphereAvatar from '../../sphere-avatar/sphere-avatar';
import type { GlyphColor } from '../../../../types/glyph';
import { formatAbsoluteTime } from '../SphereTimelineEventMolecule';

interface SphereCreatedContentProps {
  event: TimelineEvent;
}

interface CreatorInfo {
  name: string;
  glyph?: string;
  glyphColor?: GlyphColor;
  imageId?: string;
  sphereId: string;
}

const SphereCreatedContent: React.FC<SphereCreatedContentProps> = ({ event }) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const { payload, actor, timestamp } = event;

  const creatorId = (payload?.additionalData?.createdBy as string | undefined) || actor;
  // Phase 3: use payload name when available; getSphereData is avatar-only (graceful fallback
  // for name when the event was created before createdByName was stored in the payload).
  const creatorNameFromPayload = payload?.additionalData?.createdByName as string | undefined;
  const sphereType = payload?.sphereType || payload?.sphere?.properties?.type;

  // Initialize with payload name for immediate display when available
  const [creator, setCreator] = useState<CreatorInfo | null>(
    creatorNameFromPayload ? { name: creatorNameFromPayload, sphereId: creatorId || '' } : null,
  );

  useEffect(() => {
    if (!creatorId || !apiClients?.graphClient) return;

    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(creatorId)) {
      setCreator({ name: creatorId, sphereId: creatorId });
      return;
    }

    let cancelled = false;

    const fetchCreator = async () => {
      try {
        const cachedData = await globalSphereCache.getSphereData(
          creatorId,
          apiClients.graphClient
        );
        if (cancelled) return;

        const sphere = cachedData.info?.sphere;
        if (sphere) {
          setCreator({
            // Payload name wins; getSphereData provides avatar data + name for older events
            name: creatorNameFromPayload || sphere.properties?.name || creatorId,
            glyph: sphere.properties?.glyph,
            glyphColor: sphere.properties?.glyphColor,
            imageId: sphere.properties?.imageId,
            sphereId: sphere.id || creatorId,
          });
        } else {
          setCreator({ name: creatorNameFromPayload || creatorId, sphereId: creatorId });
        }
      } catch {
        if (!cancelled) {
          setCreator({ name: creatorNameFromPayload || creatorId, sphereId: creatorId });
        }
      }
    };

    fetchCreator();

    return () => {
      cancelled = true;
    };
  }, [creatorId, creatorNameFromPayload, apiClients]);

  // Format the sphere type for display (e.g. "center_task" -> "Task")
  const formatSphereType = (type: string | undefined): string => {
    if (!type) return t('common.unknown');
    return type
      .replace(/^center_/, '')
      .replaceAll('_', ' ')
      .replaceAll(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <div className="sphere-timeline-event__content-block">
      {/* Created by with avatar */}
      <div className="sphere-timeline-event__meta-row">
        <i className="pi pi-user" />
        <span className="sphere-timeline-event__meta-label">
          {t('timeline.eventContent.createdBy')}
        </span>
        <span className="sphere-timeline-event__meta-value sphere-timeline-event__creator">
          {creator ? (
            <>
              <SphereAvatar
                glyph={creator.glyph}
                glyphColor={creator.glyphColor}
                sphereId={creator.sphereId}
                sphereName={creator.name}
                imageId={creator.imageId}
                size="xsmall"
                className="sphere-timeline-event__creator-avatar"
              />
              <span>{creator.name}</span>
            </>
          ) : (
            <span>{creatorId || t('common.unknown')}</span>
          )}
        </span>
      </div>

      {/* Created at (absolute time) */}
      <div className="sphere-timeline-event__meta-row">
        <i className="pi pi-calendar" />
        <span className="sphere-timeline-event__meta-label">
          {t('timeline.eventContent.createdAt')}
        </span>
        <span className="sphere-timeline-event__meta-value">
          {formatAbsoluteTime(timestamp)}
        </span>
      </div>

      {/* Sphere type */}
      {sphereType && (
        <div className="sphere-timeline-event__meta-row">
          <i className="pi pi-tag" />
          <span className="sphere-timeline-event__meta-label">
            {t('timeline.eventContent.sphereType')}
          </span>
          <span className="sphere-timeline-event__meta-value">
            {formatSphereType(sphereType)}
          </span>
        </div>
      )}
    </div>
  );
};

export default SphereCreatedContent;
